import { Component, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import FormRequest from '../common/formRequest';
import { FiscoService } from 'src/app/app-core/services/fisco.service';
import { LocalStorageService } from 'src/app/app-core/services/local-storage.service';
import { DIGITAL_FORM_FIELD_MAX_LENGTH, DigitalFormFieldTypeEnum } from 'src/app/Enums/DigitalFormEnum';
import { buildDigitalFormPages, fieldWidthClass } from 'src/app/app-core/lib/digital-form-layout';
import { IDigitalForm, IDigitalFormField } from 'src/app/interfaces/digital-form';
import { GenericValidator } from '../utilities/genericValidator';

/** RG varia por estado: números com ponto, hífen ou barra, e às vezes um X no dígito. A API valida o mesmo. */
const RG_PATTERN = /^(?=.*\d)[0-9A-Za-z][0-9A-Za-z.\-\/ ]*$/;

/** Respostas guardadas no rascunho da solicitação, com o formulário e a versão a que pertencem. */
interface IDigitalFormDraft {
    formularioId: number;
    versao: number;
    respostas: Record<string, any>;
}

/**
 * Renderiza o formulário digital configurado pelo administrador para o assunto.
 *
 * Os campos são criados a partir da estrutura salva, na ordem definida, e nomeados pelo
 * código do campo — é essa chave que o backend usa para validar as respostas contra o
 * snapshot. Formulários longos são divididos em etapas, percorridas pelos mesmos botões
 * "Voltar" e "Prosseguir" da solicitação, para não haver dois conjuntos de botões.
 */
@Component({
    selector: 'app-form-request-dynamic',
    templateUrl: './form-request-dynamic.component.html',
    styleUrls: ['./form-request-dynamic.component.scss'],
    providers: [LocalStorageService],
})
export class FormRequestDynamicComponent extends FormRequest implements OnChanges {
    @Input() subject: string;
    @Input() digitalForm: IDigitalForm | null = null;

    public readonly fieldTypeEnum = DigitalFormFieldTypeEnum;
    public readonly widthClass = fieldWidthClass;

    public pages: IDigitalFormField[][] = [[]];
    public pageIndex = 0;

    private changesBound = false;
    private localStorageKey = 'form-request-dynamic';

    constructor(
        public fb: FormBuilder,
        public fiscoService: FiscoService,
        private host: ElementRef<HTMLElement>,
        private localStorageService: LocalStorageService,
    ) {
        super(fb, fiscoService);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['digitalForm']) {
            this.buildForm();
        }
    }

    // ------------------------------------------------------------------
    // Construção
    // ------------------------------------------------------------------

    private buildForm(): void {
        const campos = [...(this.digitalForm?.campos || [])].sort((a, b) => a.AFC_Ordem - b.AFC_Ordem);

        this.pages = buildDigitalFormPages(campos);
        this.pageIndex = 0;

        const group: Record<string, any> = {};

        for (const campo of campos) {
            const validators: ValidatorFn[] = campo.AFC_Obrigatorio ? [Validators.required] : [];
            const maxLength = this.maxLength(campo);

            if (maxLength) validators.push(Validators.maxLength(maxLength));
            if (campo.AFC_Tipo === DigitalFormFieldTypeEnum.CPF) validators.push(GenericValidator.isValidCpfOrCnpj());
            if (campo.AFC_Tipo === DigitalFormFieldTypeEnum.RG) validators.push(Validators.pattern(RG_PATTERN));

            group[this.controlName(campo)] = [null, validators];
        }

        group['termoAceito'] = [null, Validators.requiredTrue];

        // Lido antes do setControl: num formulário recarregado, a troca já grava o grupo vazio.
        const rascunho = this.getFromLocalStorage();

        const formVariants: FormGroup = this.fb.group(group);
        this.form.setControl('formVariants', formVariants);

        if (rascunho) formVariants.patchValue(rascunho);

        // Emissão própria (e não a do FormRequest) para levar junto a etapa atual.
        if (!this.changesBound) {
            this.form.valueChanges.subscribe(() => {
                this.saveOnLocalStorage();
                this.emitState();
            });
            this.changesBound = true;
        }

        this.emitState();
    }

    // ------------------------------------------------------------------
    // Rascunho
    // ------------------------------------------------------------------

    /**
     * Respostas salvas deste formulário, na mesma versão. Ao editar o formulário o
     * administrador recria os campos com outros códigos, então um rascunho de outra versão
     * não tem onde entrar. O termo de veracidade fica de fora: é aceito no envio.
     */
    private getFromLocalStorage(): Record<string, any> | null {
        const storageData = this.localStorageService.get<IDigitalFormDraft>(this.localStorageKey);

        if (!storageData || typeof storageData !== 'object' || !this.digitalForm) return null;

        const mesmoFormulario =
            storageData.formularioId === this.digitalForm.AFR_CodigoFormulario &&
            storageData.versao === this.digitalForm.AFR_Versao;

        return mesmoFormulario ? storageData.respostas || null : null;
    }

    private saveOnLocalStorage(): void {
        if (!this.digitalForm?.AFR_CodigoFormulario) return;

        const { termoAceito, ...respostas } = this.form.get('formVariants')?.getRawValue() || {};

        this.localStorageService.set(this.localStorageKey, {
            formularioId: this.digitalForm.AFR_CodigoFormulario,
            versao: this.digitalForm.AFR_Versao,
            respostas,
        } as IDigitalFormDraft);
    }

    public clearLocalStorage(): void {
        this.localStorageService.remove(this.localStorageKey);
    }

    /** Publica validade da etapa atual e do formulário inteiro para o passo da solicitação. */
    private emitState(): void {
        this.formChange.emit({
            form: this.form.getRawValue(),
            validators: this.form.valid,
            pageValid: this.isCurrentPageValid,
            hasNextPage: this.hasNextPage,
        });
    }

    // ------------------------------------------------------------------
    // Etapas
    // ------------------------------------------------------------------

    public get campos(): IDigitalFormField[] {
        return this.pages[this.pageIndex] || [];
    }

    public get totalPages(): number {
        return this.pages.length;
    }

    public get isLastPage(): boolean {
        return this.pageIndex === this.totalPages - 1;
    }

    public get hasNextPage(): boolean {
        return !this.isLastPage;
    }

    public get isCurrentPageValid(): boolean {
        const camposValidos = this.campos.every(campo => this.control(campo)?.valid !== false);

        return this.isLastPage ? camposValidos && this.form.valid : camposValidos;
    }

    /** Avança uma etapa. Devolve false quando já está na última, para o passo seguir adiante. */
    public goToNextPage(): boolean {
        if (!this.hasNextPage) return false;

        if (!this.isCurrentPageValid) {
            this.markPageAsTouched();
            return true;
        }

        this.goToPage(this.pageIndex + 1);
        return true;
    }

    /** Volta uma etapa. Devolve false quando já está na primeira. */
    public goToPreviousPage(): boolean {
        if (this.pageIndex === 0) return false;

        this.goToPage(this.pageIndex - 1);
        return true;
    }

    /** Só permite ir direto para etapas já percorridas. */
    public goToPage(index: number): void {
        if (index < 0 || index >= this.totalPages || index > this.pageIndex + 1) return;

        this.pageIndex = index;
        this.emitState();
        this.host.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    public isPageComplete(index: number): boolean {
        return (this.pages[index] || []).every(campo => this.control(campo)?.valid !== false);
    }

    private markPageAsTouched(): void {
        this.campos.forEach(campo => this.control(campo)?.markAsTouched());
    }

    // ------------------------------------------------------------------
    // Campos
    // ------------------------------------------------------------------

    public controlName(campo: IDigitalFormField): string {
        return String(campo.AFC_CodigoCampo);
    }

    private control(campo: IDigitalFormField) {
        return this.form.get(['formVariants', this.controlName(campo)]);
    }

    public isInvalid(campo: IDigitalFormField): boolean {
        const control = this.control(campo);

        return !!control && control.invalid && (control.dirty || control.touched);
    }

    public maxLength(campo: IDigitalFormField): number | null {
        return DIGITAL_FORM_FIELD_MAX_LENGTH[campo.AFC_Tipo] ?? null;
    }

    public errorMessage(campo: IDigitalFormField): string {
        const errors = this.control(campo)?.errors || {};

        if (errors['maxlength']) return `Máximo de ${errors['maxlength'].requiredLength} caracteres.`;
        if (errors['cpfNotValid'] || errors['cnpjNotValid'] || errors['cpfOrCnpjNotValid'] || errors['mask']) {
            return 'Informe um CPF ou CNPJ válido.';
        }
        if (errors['pattern']) return 'Use números e letras, com ponto, hífen ou barra, como no documento.';

        return 'Campo obrigatório.';
    }

    /** RG em maiúsculas e sem espaços sobrando, como sai no requerimento. */
    public normalizeRg(campo: IDigitalFormField): void {
        const control = this.control(campo);
        const valor = control?.value;

        if (typeof valor !== 'string') return;

        const normalizado = valor.toUpperCase().replace(/\s+/g, ' ').trim();

        if (normalizado !== valor) control.setValue(normalizado);
    }

    public options(campo: IDigitalFormField): string[] {
        return (campo.AFC_Opcoes || []).filter(opcao => (opcao || '').trim().length > 0);
    }

    public trackByCampo(_: number, campo: IDigitalFormField): number {
        return campo.AFC_CodigoCampo || _;
    }

    public trackByIndex(index: number): number {
        return index;
    }

    // ------------------------------------------------------------------
    // Textos
    // ------------------------------------------------------------------

    public get formTitle(): string {
        return this.digitalForm?.AFR_Titulo || this.subject || 'Requerimento';
    }

    public get requiresResponsibleSignature(): boolean {
        return this.digitalForm?.AFR_ExigeAssinaturaResponsavel === true;
    }

    /** O botão de avançar fica desabilitado enquanto a etapa é inválida: explica por quê. */
    public get pendingHint(): string | null {
        if (this.isCurrentPageValid) return null;

        const faltamCampos = this.campos.some(campo => this.control(campo)?.invalid);

        if (!this.isLastPage || !this.form.get(['formVariants', 'termoAceito'])?.invalid) {
            return 'Preencha os campos obrigatórios (*) para continuar.';
        }

        return faltamCampos
            ? 'Preencha os campos obrigatórios (*) e aceite o termo para prosseguir.'
            : 'Aceite o termo de veracidade para prosseguir.';
    }
}
